/**
 * AchievementService - Manages badges and achievements
 * 
 * Handles badge unlocking, checking conditions, and notifications.
 */

import { storageService } from './StorageService';
import { configService } from './ConfigService';

class AchievementService {
  constructor() {
    this.pendingNotifications = [];
  }

  /**
   * Check and unlock badges based on current state
   * Returns array of newly unlocked badges
   */
  checkAndUnlockBadges() {
    const badges = configService.getBadges();
    const newlyUnlocked = [];

    for (const badge of badges) {
      if (!storageService.isBadgeUnlocked(badge.id)) {
        if (this.checkCondition(badge.unlockCondition)) {
          this.unlockBadge(badge.id);
          newlyUnlocked.push(badge);
        }
      }
    }

    return newlyUnlocked;
  }

  /**
   * Check a single unlock condition
   */
  checkCondition(condition) {
    if (!condition) return false;

    const { type, value } = condition;

    switch (type) {
      case 'lessonComplete':
        return storageService.isLessonCompleted(value);

      case 'lessonsCompleted':
        return storageService.getCompletedLessons().length >= value;

      case 'challengeComplete':
        return storageService.isChallengeCompleted(value);

      case 'challengesCompleted':
        return storageService.getCompletedChallenges().length >= value;

      case 'points':
        return storageService.getTotalPoints() >= value;

      case 'streak':
        return storageService.getCurrentStreak() >= value;

      case 'level':
        const totalPoints = storageService.getTotalPoints();
        const currentLevel = configService.getLevelForPoints(totalPoints);
        return currentLevel.level >= value;

      case 'codeExecutions':
        const stats = storageService.getStatistics();
        return (stats.totalCodeExecutions || 0) >= value;

      case 'category':
        // Complete all lessons in a category
        const completedLessons = storageService.getCompletedLessons();
        const categoryLessons = configService.getLessonsByCategory(value);
        return categoryLessons.every(lesson => completedLessons.includes(lesson.id));

      case 'perfectLesson':
        // This would be set when a lesson is completed with perfect score
        const perfectLessons = storageService.get('perfect_lessons', []);
        return perfectLessons.includes(value);

      case 'firstTry':
        // Complete a lesson on first try
        const firstTryLessons = storageService.get('first_try_lessons', []);
        return firstTryLessons.includes(value);

      // 1. categoryComplete - Complete all lessons in a category
      // Used by: JavaScript, Python, Java, C, C++ (all languages)
      case 'categoryComplete':
        const completedLessonsForCat = storageService.getCompletedLessons();
        const categoryLessonsToCheck = configService.getLessonsByCategory(value);
        return categoryLessonsToCheck.length > 0 &&
               categoryLessonsToCheck.every(lesson => completedLessonsForCat.includes(lesson.id));

      // 2. perfectQuiz / perfectQuizzes - Complete quizzes with 100% score
      // Used by: JavaScript, Python, Java, C, C++ (all languages)
      case 'perfectQuiz':
      case 'perfectQuizzes':
        const perfectQuizCount = storageService.get('perfect_quizzes_count', 0);
        return perfectQuizCount >= value;

      // 3. fastLesson / challengeSpeedRun - Complete a lesson/challenge quickly
      // Used by: JavaScript, Python, Java, C, C++ (all languages)
      case 'fastLesson':
      case 'challengeSpeedRun':
        const fastCompletions = storageService.get('fast_completions', []);
        return fastCompletions.some(fc => fc.duration <= value);

      // 4. interviewsReviewed / interviewQuestionsViewed - Review interview questions
      // Used by: JavaScript, Java
      case 'interviewsReviewed':
      case 'interviewQuestionsViewed':
        const reviewedInterviews = storageService.get('interviews_reviewed', []);
        return reviewedInterviews.length >= value;

      // 5. allChallengesComplete - Complete ALL challenges
      // Used by: Python
      case 'allChallengesComplete':
        const allChallengesList = configService.getChallengesList();
        const completedChallengesList = storageService.getCompletedChallenges();
        return allChallengesList.length > 0 &&
               allChallengesList.every(c => completedChallengesList.includes(c.id));

      default:
        console.warn(`Unknown badge condition type: ${type}`);
        return false;
    }
  }

  /**
   * Manually unlock a badge
   */
  unlockBadge(badgeId) {
    const badge = configService.getBadgeById(badgeId);
    if (!badge) {
      console.warn(`Badge not found: ${badgeId}`);
      return null;
    }

    if (storageService.isBadgeUnlocked(badgeId)) {
      return null; // Already unlocked
    }

    storageService.unlockBadge(badgeId);

    // Award badge points
    if (badge.points) {
      storageService.addPoints(badge.points);
    }

    // Add to pending notifications
    this.pendingNotifications.push({
      type: 'badge',
      badge: badge,
      unlockedAt: Date.now()
    });

    return badge;
  }

  /**
   * Get all badges with unlock status
   */
  getAllBadges() {
    const badges = configService.getBadges();
    const unlockedBadges = storageService.getUnlockedBadges();

    return badges.map(badge => ({
      ...badge,
      unlocked: unlockedBadges.includes(badge.id),
      unlockedAt: storageService.getBadgeUnlockTime(badge.id)
    }));
  }

  /**
   * Get only unlocked badges
   */
  getUnlockedBadges() {
    return this.getAllBadges().filter(badge => badge.unlocked);
  }

  /**
   * Get only locked badges
   */
  getLockedBadges() {
    return this.getAllBadges().filter(badge => !badge.unlocked);
  }

  /**
   * Get badges by rarity
   */
  getBadgesByRarity(rarity) {
    return this.getAllBadges().filter(badge => badge.rarity === rarity);
  }

  /**
   * Get badge progress (how close to unlocking)
   */
  getBadgeProgress(badgeId) {
    const badge = configService.getBadgeById(badgeId);
    if (!badge || !badge.unlockCondition) return null;

    const { type, value } = badge.unlockCondition;
    let current = 0;
    let target = value;

    switch (type) {
      case 'lessonsCompleted':
        current = storageService.getCompletedLessons().length;
        break;
      case 'challengesCompleted':
        current = storageService.getCompletedChallenges().length;
        break;
      case 'points':
        current = storageService.getTotalPoints();
        break;
      case 'streak':
        current = storageService.getCurrentStreak();
        break;
      case 'codeExecutions':
        const stats = storageService.getStatistics();
        current = stats.totalCodeExecutions || 0;
        break;
      case 'categoryComplete':
        const catLessons = configService.getLessonsByCategory(badge.unlockCondition.value);
        const completedInCat = storageService.getCompletedLessons().filter(
          lessonId => catLessons.some(l => l.id === lessonId)
        ).length;
        current = completedInCat;
        target = catLessons.length;
        break;
      case 'perfectQuiz':
      case 'perfectQuizzes':
        current = storageService.get('perfect_quizzes_count', 0);
        break;
      case 'interviewsReviewed':
      case 'interviewQuestionsViewed':
        current = storageService.get('interviews_reviewed', []).length;
        break;
      default:
        return null;
    }

    return {
      badgeId,
      current,
      target,
      percentage: Math.min(100, Math.round((current / target) * 100)),
      remaining: Math.max(0, target - current)
    };
  }

  /**
   * Get pending badge notifications and clear them
   */
  getPendingNotifications() {
    const notifications = [...this.pendingNotifications];
    this.pendingNotifications = [];
    return notifications;
  }

  /**
   * Check if there are pending notifications
   */
  hasPendingNotifications() {
    return this.pendingNotifications.length > 0;
  }

  /**
   * Get achievement statistics
   */
  getStats() {
    const allBadges = this.getAllBadges();
    const unlocked = allBadges.filter(b => b.unlocked);
    
    const byRarity = {
      common: { total: 0, unlocked: 0 },
      uncommon: { total: 0, unlocked: 0 },
      rare: { total: 0, unlocked: 0 },
      epic: { total: 0, unlocked: 0 },
      legendary: { total: 0, unlocked: 0 }
    };

    allBadges.forEach(badge => {
      const rarity = badge.rarity || 'common';
      if (byRarity[rarity]) {
        byRarity[rarity].total++;
        if (badge.unlocked) {
          byRarity[rarity].unlocked++;
        }
      }
    });

    return {
      total: allBadges.length,
      unlocked: unlocked.length,
      locked: allBadges.length - unlocked.length,
      percentage: allBadges.length > 0 
        ? Math.round((unlocked.length / allBadges.length) * 100) 
        : 0,
      totalPoints: unlocked.reduce((sum, b) => sum + (b.points || 0), 0),
      byRarity
    };
  }

  /**
   * Record a perfect lesson completion
   */
  recordPerfectLesson(lessonId) {
    const perfectLessons = storageService.get('perfect_lessons', []);
    if (!perfectLessons.includes(lessonId)) {
      perfectLessons.push(lessonId);
      storageService.set('perfect_lessons', perfectLessons);
    }
  }

  /**
   * Record a first-try lesson completion
   */
  recordFirstTryLesson(lessonId) {
    const firstTryLessons = storageService.get('first_try_lessons', []);
    if (!firstTryLessons.includes(lessonId)) {
      firstTryLessons.push(lessonId);
      storageService.set('first_try_lessons', firstTryLessons);
    }
  }
}

// Export singleton instance
export const achievementService = new AchievementService();
export default achievementService;